// GTM integration — see ./LEGACY_NOTES.md for the full legacy parity audit.
//
// Invocation surface:
//   getHead(config)              — SSR/static render: injects the GTM <script> + <noscript> snippets.
//   setup(pipe, config)          — once per pipe load: stores pipe ref, writes legacy localStorage mirror,
//                                  subscribes to navigation:show / navigation:exit-intent.
//   navigation:show subscriber   — every page transition: re-writes localStorage mirror, then pushes
//                                  `page viewed` (including the initial landing).
//   navigation:exit-intent sub.  — the detector decided the visitor is leaving: pushes
//                                  `exit intent triggered` with the mechanic that fired.
//   gtm_event processor          — user-wired events (button clicks, etc.): _push(eventName, params).
//                                  `params` is an EXTENSION map; defaults are always merged underneath.
//
// Missing vs. legacy (intentional gaps — wire up when pipehouse exposes the underlying state):
//   pipe_series / pipeSeries     — TODO: hardcoded to 'pipehouse' for now. Pipehouse has no real "series"
//                                  concept yet; swap to the actual source when one lands.
//   offer-price OPAQUE `trackingProperties` blob — legacy `order completed` / `plan selected`
//                                  pushes spread the offer-price record's user-defined
//                                  `trackingProperties` object (admin-authored, free-form) on top of
//                                  every event. OfferConst (api/types.gen.ts) has no such field, so
//                                  that opaque admin blob is unrecoverable in pipehouse. The new
//                                  payment:* payloads ship instead:
//                                    (a) the full structured OfferConst surface (ppoId, paymentType,
//                                        base/trial price + period + discount, currency, orderId,
//                                        eventId, isExitIntent), resolved from
//                                        `var.global.selectedOfferGroup` + `var.global.hasExitIntentBeenAccepted`;
//                                    (b) a constructed legacy-shaped attribution blob (fb_ads_id,
//                                        fb_adset_id, cookies, origin_query_string, query_string)
//                                        from `buildLegacyTrackingProperties` — same shape as
//                                        legacy `payments.js`'s `trackingProperties`. Spread at top
//                                        level in the GTM subscriber to mirror legacy `_push`.
//                                  TODO: if the admin-authored blob is still required, add
//                                  `trackingProperties?: Record<string, unknown>` to OfferConst + its
//                                  API DTO + builder editor, then spread it in
//                                  `buildPaymentEventPayload`.

import type { PipeIntegration, PipeIntegrationDef } from '../index'
import type { PipeInterface, PipeEventProcessorDef } from '@render/composables/usePipe'
import {
  PAYMENT_EVENTS,
  buildPaymentEventPayload,
  type PaymentEventPayload,
} from '@render/composables/usePaymentEvents'
import { COOKIE_CONSENT_STORAGE_KEY } from '@render/lib/cookieConsent'
import {
  PAYMENT_SESSION_KEY,
  getPaymentSessionId,
  type PaymentSessionState,
} from '@render/composables/paymentSessionStore'
import type { ExitIntentTrigger } from '@render/composables/useExitIntent'
import type { ComponentNode } from '@api/types.gen'
import {
  CHECKOUT_COMPONENT_TYPE,
  CHECKOUT_VERSION_CONSTANT,
  normalizeCheckoutProvider,
  resolveCheckoutProvider as resolveCheckoutNodeProvider,
  type CheckoutProvider,
} from '@render/integrations'

interface GtmConfig {
  containerId: string
  // Injected at build time as `value === true` from the distribution-resolved
  // `const.global.cookieConsentEnabled` by GTM's resolveBuildConfig (declared on
  // its IntegrationRegistry entry in render/integrations/index.ts, applied by
  // builder/lib/integrations.ts) — so a real build always carries a concrete
  // boolean. When false, getHead omits the Google Consent Mode default entirely
  // (pre-feature behavior — tags fire freely). Undefined only in the editor
  // preview (the build plugin doesn't run there) → treated as enabled, so the
  // consent default is shown as today.
  cookieConsentEnabled?: boolean
}

interface NavigationPayload {
  pageId: string
  path: string
  title: string
  timestamp: number
  // Only on `navigation:exit-intent` — which mechanic decided the visitor is leaving.
  trigger?: ExitIntentTrigger
}

interface GtmEventData {
  eventName: string
  params?: Record<string, unknown>
}

declare global {
  interface Window {
    dataLayer?: Record<string, unknown>[]
    // Defined by the consent-default head script in getHead() below.
    gtag?: (...args: unknown[]) => void
  }
}

const utmKeyMap: Record<string, string> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  utm_source: 'sourceUtm',
  utm_medium: 'mediumUtm',
  utm_campaign: 'campaignUtm',
  utm_term: 'termUtm',
  utm_content: 'contentUtm',
  fb_ads_id: 'fbAdsId',
  fb_adset_id: 'fbAdsetId',
  /* eslint-enable @typescript-eslint/naming-convention */
}

function normalizeUtmKey(key: string): string {
  return String(key)
    .trim()
    .replace(/\s+/g, '')
    .replace(/[-.]/g, '_')
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/__+/g, '_')
    .toLowerCase()
}

function resolveUtmParams(queryParams: Record<string, string>): Record<string, string> {
  const normalized: Record<string, string> = {}
  for (const [k, v] of Object.entries(queryParams)) {
    normalized[normalizeUtmKey(k)] = v
  }
  const out: Record<string, string> = {}
  for (const [src, dst] of Object.entries(utmKeyMap)) {
    const value = normalized[src]
    if (value !== undefined) out[dst] = value
  }
  return out
}

function derivePageCode(
  pagePath: string | undefined,
  distributionPath: string | undefined,
): string | null {
  if (!pagePath) return null
  const stripped = distributionPath ? (pagePath.split(distributionPath)[1] ?? pagePath) : pagePath
  const segments = stripped.split('/').filter(Boolean)
  return segments[0] ?? null
}

function readClaritySessionId(cookies: Record<string, string> | undefined): string | null {
  const raw = cookies?._clsk
  if (!raw) return null
  return raw.split('^')[0] || null
}

const CHECKOUT_PROVIDER_COMPONENT_TYPES: Record<string, 'primer' | 'yuno'> = {
  PrimerCheckout: 'primer',
  YunoCheckout: 'yuno',
}
const CHECKOUT_PROVIDER_COUNT = new Set(Object.values(CHECKOUT_PROVIDER_COMPONENT_TYPES)).size

interface CheckoutScan {
  providers: Set<CheckoutProvider>
  /** A `Checkout` node was seen: it renders the constant's provider, or the fallback. */
  followsConstant: boolean
}

// Once every provider — and a constant-following Checkout — has been seen the
// scan cannot learn anything more.
const scanComplete = (scan: CheckoutScan) =>
  scan.providers.size >= CHECKOUT_PROVIDER_COUNT && scan.followsConstant

// Walks a page's component tree — resolving SharedNodeRef targets via
// pipe.getSharedNodeById — collecting every checkout provider present
// (PrimerCheckout / YunoCheckout by type, a Checkout node by the provider it
// resolves to), so every GTM push on a checkout page can be tagged with which
// provider it uses. This is schema presence, not live-mounted state: a node
// behind a false visibility rule still counts. visitedSharedNodeIds guards
// against a malformed self-referencing SharedNodeRef chain.
function collectCheckoutProviders(
  pipe: PipeInterface,
  nodes: ComponentNode[] | undefined,
  visitedSharedNodeIds: Set<string>,
  out: CheckoutScan,
  checkoutVersion: unknown,
): void {
  if (!nodes) return
  for (const node of nodes) {
    if (scanComplete(out)) return
    const provider = CHECKOUT_PROVIDER_COMPONENT_TYPES[node.type]
    if (provider) out.providers.add(provider)

    if (node.type === CHECKOUT_COMPONENT_TYPE) {
      out.followsConstant = true
      out.providers.add(resolveCheckoutNodeProvider(checkoutVersion))
    }

    if (node.type === 'SharedNodeRef') {
      const sharedNodeId = node.fields?.sharedNodeId as string | undefined
      if (sharedNodeId && !visitedSharedNodeIds.has(sharedNodeId)) {
        visitedSharedNodeIds.add(sharedNodeId)
        const sharedNode = pipe.getSharedNodeById(sharedNodeId)
        collectCheckoutProviders(
          pipe,
          sharedNode?.content.children as ComponentNode[] | undefined,
          visitedSharedNodeIds,
          out,
          checkoutVersion,
        )
      }
    }

    collectCheckoutProviders(pipe, node.children, visitedSharedNodeIds, out, checkoutVersion)
  }
}

const googleTagManager: PipeIntegration = (): PipeIntegrationDef => {
  let pipeRef: PipeInterface | null = null
  // Checkout provider is a PIPE-wide property, not a per-page one: earlier
  // funnel pages (plan selection, etc.) need the same tag even though they
  // don't contain a checkout component themselves — only
  // whichever page IS the checkout page does. Scan every page once and cache
  // (the pipe's content can't change after load); `resolved` guards against
  // permanently caching `undefined` if this runs before pages have loaded.
  let cachedCheckoutProvider: CheckoutProvider | undefined
  let checkoutProviderResolved = false

  const resolveCheckoutProvider = (): CheckoutProvider | undefined => {
    if (checkoutProviderResolved) return cachedCheckoutProvider
    const pages = pipeRef?.data.value?.latestContent?.content.pages
    if (!pages || pages.length === 0) return undefined
    const checkoutVersion = pipeRef?.getData(`const.${CHECKOUT_VERSION_CONSTANT}`)
    const found: CheckoutScan = { providers: new Set(), followsConstant: false }
    for (const page of pages) {
      if (scanComplete(found)) break
      collectCheckoutProviders(
        pipeRef as PipeInterface,
        page.content.children,
        new Set(),
        found,
        checkoutVersion,
      )
    }
    // One provider in the pipe → that is the answer, whatever the constant says
    // (it is global, so its default reaches single-provider pipes too). Both
    // present → the pipe picks per distribution on the constant, and only the
    // constant knows which one this distribution renders — with the Checkout
    // fallback when a constant-following Checkout is what renders it.
    cachedCheckoutProvider =
      found.providers.size > 1
        ? found.followsConstant
          ? resolveCheckoutNodeProvider(checkoutVersion)
          : normalizeCheckoutProvider(checkoutVersion)
        : [...found.providers][0]
    checkoutProviderResolved = true
    return cachedCheckoutProvider
  }

  // payments-api — not the frontend — decides which provider actually charges
  // a given customer (e.g. a saved card can route to either Primer or Yuno),
  // so this deliberately does NOT branch on resolveCheckoutProvider(): usePrimer
  // / useYuno / SavedCard all record into the same provider-agnostic
  // `var.global.paymentSession` (see paymentSessionStore.ts). Prefers the
  // session behind an actual completed order — most recent first, falling
  // back to the first-ever completion — over `latest` (a merely-fetched,
  // possibly-abandoned session): once something has completed, that's a more
  // useful correlation id than whatever in-progress attempt happens to be sitting
  // in `latest`.
  const resolvePaymentSessionId = (): string | null => {
    const state = pipeRef?.getData(PAYMENT_SESSION_KEY) as PaymentSessionState | null | undefined
    const record = state?.completed?.current ?? state?.completed?.initial

    return getPaymentSessionId(record)
  }

  const getTrackingProperties = () => {
    const tp =
      (pipeRef?.getData('var.global.trackingProperties') as
        | {
            queryParams?: Record<string, string>
            hashParams?: Record<string, string>
            cookies?: Record<string, string>
            fraudNetSessionId?: string
          }
        | undefined) ?? {}
    return {
      queryParams: tp.queryParams ?? {},
      hashParams: tp.hashParams ?? {},
      cookies: tp.cookies ?? {},
      fraudNetSessionId: tp.fraudNetSessionId,
    }
  }

  const getDefaults = (): Record<string, unknown> => {
    if (!pipeRef) return {}
    const distributionPath = pipeRef.getData('const.system.distributionPath') as string | undefined
    const { queryParams, cookies } = getTrackingProperties()
    // Read pathname live, not from var.global.trackingProperties.pagePath —
    // the latter is captured once on landing and never updates during navigation.
    const pagePath = typeof window !== 'undefined' ? window.location.pathname : undefined
    const pageCode = derivePageCode(pagePath, distributionPath)
    const brandCode = pipeRef.getData('const.system.brandCode') as string | undefined
    const pipeId = pipeRef.data.value?.id ?? null

    /* eslint-disable @typescript-eslint/naming-convention */
    return {
      pageCode,
      page: pageCode,
      pipe: distributionPath ?? null,
      claritySessionID: readClaritySessionId(cookies),
      pipe_id: pipeId,
      language: pipeRef.getData('const.system.language') ?? null,
      geography: pipeRef.getData('const.system.geography') ?? null,
      brand_code: brandCode ?? null,
      brand: brandCode ?? null,
      pipe_series: 'pipehouse',
      pipeSeries: 'pipehouse',
      eventSourceSystem: 'pipehouse',
      eventSourcePlatform: 'web',
      checkoutProvider: resolveCheckoutProvider() ?? null,
      paymentSessionId: resolvePaymentSessionId(),
      // Written by the pipeorders integration on every order create/update
      // response (var.global.paymentsApiCustomerId) — not a declared pipe
      // Variable, so read directly off pipe state like paymentSessionId above.
      paymentsApiCustomerId: pipeRef.getData('var.global.paymentsApiCustomerId') ?? null,
      // Set once the first order-create/update POST resolves (pipeorders.ts) —
      // absent on any page view before email capture, hence the null default.
      orderId: pipeRef.getData('var.global.orderId') ?? null,
      email: pipeRef.getData('var.global.email') ?? null,
      // Ingress AB-test trace id (see seedOriginDefaults in usePipe.ts):
      // current = most recent ingress landing's id. `origin` is intentionally
      // omitted from GTM instrumentation — only `current` is sent here (origin
      // still rides Klaviyo + the payments-API trackingProperties).
      currentIngressTraceId: pipeRef.getData('var.system.currentIngressTraceId') ?? null,
      ...resolveUtmParams(queryParams),
    }
    /* eslint-enable @typescript-eslint/naming-convention */
  }

  // Self-initialize rather than only checking for the array: the getHead()
  // inline script also does `w[l]=w[l]||[]`, but on a direct page load it can
  // run after a component has already fired a push (e.g. via unhead's
  // client-side head insertion racing onMounted/navigation:show handlers).
  // Mirroring GTM's own defensive init here means an early push always lands
  // in the array — GTM drains the full backlog once gtm.js loads, so no
  // retry/replay mechanism is needed.
  const push = (eventName: string, overrides: Record<string, unknown> = {}) => {
    if (typeof window === 'undefined') return
    if (!Array.isArray(window.dataLayer)) window.dataLayer = []
    try {
      window.dataLayer.push({ event: eventName, ...getDefaults(), ...overrides })
    } catch (err) {
      console.warn(`[GTM] Failed to push event ${eventName}:`, err)
    }
  }

  // Mirror legacy `${pathPrefix}:data` localStorage so existing GTM tag scripts
  // can keep reading pipeline state directly. See ./LEGACY_NOTES.md.
  const writeLegacyDataMirror = () => {
    if (typeof window === 'undefined' || !pipeRef) return
    const distributionPath = pipeRef.getData('const.system.distributionPath') as string | undefined
    if (!distributionPath) return
    const { queryParams, cookies, fraudNetSessionId } = getTrackingProperties()
    const queryString = window.location.search

    const values: Record<string, unknown> = {
      ...cookies,
      ...queryParams,
      /* eslint-disable @typescript-eslint/naming-convention */
      // FRAUDNET's id; the localStorage key is where legacy funnels kept theirs.
      fraud_net_session_id:
        fraudNetSessionId || window.localStorage?.getItem('fraud_net_session_id') || '',
      /* eslint-enable @typescript-eslint/naming-convention */
      domain: document.location.host,
      cookies,
    }
    if (queryString) values.query_string = queryString
    const originQueryString = pipeRef.getData('var.system.originQueryString') as string | undefined
    if (originQueryString) values.origin_query_string = originQueryString

    try {
      window.localStorage.setItem(`${distributionPath}:data`, JSON.stringify({ values }))
    } catch (err) {
      console.warn('[GTM] Failed to write legacy localStorage mirror:', err)
    }
  }

  return {
    getHead(config: GtmConfig) {
      if (!config.containerId) return {}
      // Google Consent Mode v2 DEFAULT — must run BEFORE the GTM container
      // (tagPriority 999 < 1000; unhead sorts ascending by weight). Defines
      // dataLayer + the global gtag() stub, then defaults all four v2
      // signals from the dedicated cookie-consent localStorage key:
      // 'granted' for a returning acceptor (no flicker), else 'denied' for
      // first-time / rejected visitors. The set_cookie_consent processor
      // fires the matching gtag('consent','update',...) on Accept/Reject.
      const consentDefaultScript = {
        innerHTML: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;var __cc='denied';try{var __r=window.localStorage.getItem('${COOKIE_CONSENT_STORAGE_KEY}');if(__r){var __p=JSON.parse(__r);if(__p&&__p.state==='granted')__cc='granted';}}catch(e){}gtag('consent','default',{ad_storage:__cc,ad_user_data:__cc,ad_personalization:__cc,analytics_storage:__cc});`,
        tagPriority: 999,
      }
      const containerScript = {
        innerHTML: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${config.containerId}');`,
        tagPriority: 1000,
      }
      // Per-distribution feature flag: when cookie consent is disabled for this
      // build, omit the Consent Mode default entirely — pre-feature behavior, so
      // Google tags fire freely with no consent gating. `cookieConsentEnabled`
      // is injected at build time by GTM's resolveBuildConfig (render/integrations/
      // index.ts, applied by builder/lib/integrations.ts) from the
      // distribution-resolved const.global.cookieConsentEnabled. Absent → enabled.
      const script =
        config.cookieConsentEnabled === false
          ? [containerScript]
          : [consentDefaultScript, containerScript]
      return {
        script,
        noscript: [
          {
            innerHTML: `<iframe src="https://www.googletagmanager.com/ns.html?id=${config.containerId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`,
            tagPosition: 'bodyOpen' as const,
          },
        ],
      }
    },

    setup(pipe: PipeInterface) {
      if (typeof window === 'undefined') return
      pipeRef = pipe
      writeLegacyDataMirror()

      pipe.subscribe('navigation:show', async (payload: NavigationPayload) => {
        // Wait for the localStorage merge + seedOriginDefaults before reading
        // pipe state: the initial navigation:show fires from the route page's
        // onMounted, BEFORE loadPersistedData's deferred merge runs, so a
        // synchronous push here would freeze pre-merge values (e.g. null
        // ingress trace ids) into the dataLayer. Matches the Klaviyo/Segment
        // navigation:show subscribers.
        await pipe.whenReady()
        writeLegacyDataMirror()
        // funnelMarker (singular, first marker) is kept alongside funnelMarkers
        // so existing single-marker pages push a byte-identical payload —
        // Marketing's GTM container may already trigger on that exact field.
        const funnelMarkers = pipe.getPageById(payload.pageId)?.funnelMarkers ?? undefined
        push('page viewed', { funnelMarker: funnelMarkers?.[0] ?? '', funnelMarkers })
      })

      // Exit intent — pushed for every detector fire, including on a page that
      // wires no onExitIntent handler (the visitor still tried to leave), so
      // GA4 counts attempts rather than only the ones a pipe answered.
      // `exitIntentTrigger` names the mechanic and matches the variable authors
      // branch on; it is null only if the emitter omitted it.
      pipe.subscribe('navigation:exit-intent', (payload: NavigationPayload) => {
        const funnelMarkers = pipe.getPageById(payload.pageId)?.funnelMarkers ?? undefined
        push('exit intent triggered', {
          exitIntentTrigger: payload.trigger ?? null,
          funnelMarker: funnelMarkers?.[0] ?? '',
          funnelMarkers,
        })
      })

      // Google Consent Mode v2 UPDATE — fired by the `set_cookie_consent`
      // event processor on Accept/Reject. Keeps all gtag code in the GTM
      // integration (the consent DEFAULT is emitted in getHead). gtag is
      // defined by that head script. All four v2 signals move in lockstep.
      pipe.subscribe('cookie-consent:changed', (decision: 'granted' | 'denied') => {
        const signal = decision === 'granted' ? 'granted' : 'denied'
        window.gtag?.('consent', 'update', {
          /* eslint-disable @typescript-eslint/naming-convention */
          ad_storage: signal,
          ad_user_data: signal,
          ad_personalization: signal,
          analytics_storage: signal,
          /* eslint-enable @typescript-eslint/naming-convention */
        })
      })

      // Fired by the `clear_all_pipe_data` event processor after
      // `pipe.clearPersistedData()`. Drop the legacy `${distributionPath}:data`
      // mirror first, then re-write it with whatever pipe state is still
      // valid. Defer the re-write to a macrotask so any sibling
      // `pipe:data-cleared` subscriber that re-captures pipe state (notably
      // the trackingProperties integration) has flushed before
      // writeLegacyDataMirror reads `var.global.trackingProperties` —
      // subscriber order is integration-registration dependent.
      pipe.subscribe('pipe:data-cleared', () => {
        const distributionPath = pipe.getData('const.system.distributionPath') as string | undefined
        if (distributionPath) {
          try {
            window.localStorage.removeItem(`${distributionPath}:data`)
          } catch (err) {
            console.warn('[GTM] Failed to remove legacy localStorage mirror:', err)
          }
        }
        setTimeout(() => writeLegacyDataMirror(), 0)
      })

      // Payment lifecycle → legacy GTM events. Payload shape comes from
      // usePaymentEvents (consistent across OfferSelect, PrimerCheckout, SavedCard);
      // we spread `trackingProperties` (legacy `payments.js`-shape attribution
      // blob) at the top level to mirror legacy `_push` behaviour, where the
      // offer-price's `trackingProperties` blob was spread on every GTM push.
      const pushPayment = (eventName: string, payload: PaymentEventPayload) => {
        // checkoutProvider is only set on the events that carry it explicitly
        // (payment:checkout-requested/loaded/loadedConfirmation/abandoned). Omit
        // it from `rest` when absent so `push()`'s page-derived default isn't
        // clobbered by an explicit `undefined` key winning the spread.
        const { trackingProperties, checkoutProvider, ...rest } = payload
        push(eventName, {
          ...trackingProperties,
          ...rest,
          ...(checkoutProvider ? { checkoutProvider } : {}),
        })
      }
      pipe.subscribe(PAYMENT_EVENTS.planSelected, (payload: PaymentEventPayload) => {
        pushPayment('plan selected', payload)
      })
      pipe.subscribe(PAYMENT_EVENTS.checkoutCompleted, (payload: PaymentEventPayload) => {
        // Cumulative funnel totals (not part of the shared PaymentEventPayload,
        // which is per-transaction) — recordPurchase has already updated both
        // by the time this handler runs, since emit/subscribe is synchronous.
        // See usePurchasedPpos.ts.
        const orderCompletedPayload: PaymentEventPayload & {
          purchasedOfferTypes: string[]
          purchasedOfferSum: number
        } = {
          ...payload,
          purchasedOfferTypes:
            (pipe.getData('var.global.purchasedOfferTypes') as string[] | undefined) ?? [],
          purchasedOfferSum:
            (pipe.getData('var.global.purchasedOfferSum') as number | undefined) ?? 0,
        }
        pushPayment('order completed', orderCompletedPayload)
      })
      pipe.subscribe(PAYMENT_EVENTS.checkoutFailed, (payload: PaymentEventPayload) => {
        pushPayment('payment failed', payload)
      })
      // Provider-agnostic checkout lifecycle (fired by both PrimerCheckout and
      // YunoCheckout, tagged via payload.checkoutProvider) — lets GA4 compare
      // the two checkout implementations' funnels.
      pipe.subscribe(PAYMENT_EVENTS.checkoutRequested, (payload: PaymentEventPayload) => {
        pushPayment('checkout requested', payload)
      })
      pipe.subscribe(PAYMENT_EVENTS.checkoutLoaded, (payload: PaymentEventPayload) => {
        pushPayment('checkout loaded', payload)
      })
      pipe.subscribe(PAYMENT_EVENTS.checkoutLoadedConfirmation, (payload: PaymentEventPayload) => {
        pushPayment('checkout loaded confirmation', payload)
      })
      pipe.subscribe(PAYMENT_EVENTS.checkoutAbandoned, (payload: PaymentEventPayload) => {
        pushPayment('checkout abandoned', payload)
      })
      pipe.subscribe(
        PAYMENT_EVENTS.trackingCustomEvent,
        (payload: { eventName: string; customData?: Record<string, unknown> }) => {
          if (!payload.eventName) return

          pushPayment(
            payload.eventName.toLowerCase(),
            buildPaymentEventPayload(pipe) as PaymentEventPayload,
          )
        },
      )
      // Intentionally NOT subscribed: PAYMENT_EVENTS.checkoutVisited.
      // Legacy `/workspace/pipelines/lib/common/js/services/tracking/trackingServiceGTM.js`
      // never pushed a `dataLayer` event for VISIT_CHECKOUT or SUBMIT_CC_INPUT —
      // those status codes were shipped to the backend only (legacy
      // `dataStore.trackStatusForAnalytics(status)` ⇒ pipeorders POST in the
      // new world). Marketing's GTM tags don't expect them; emitting them
      // here would diverge from legacy. The pipeorders integration
      // (../pipeorders/pipeorders.ts) handles the backend ping for VISIT_CHECKOUT.
    },

    getEventProcessors(): PipeEventProcessorDef[] {
      return [
        {
          type: 'gtm_event',
          handler: async ({ data }) => {
            const { eventName, params } = (data as GtmEventData | undefined) ?? { eventName: '' }
            if (!eventName) return
            push(eventName, params ?? {})
          },
        },
      ]
    },
  }
}

export default googleTagManager
